import React, {useReducer} from "react";
import PropTypes from "prop-types";
import ResultNoticeService from "services/ResultNoticeService";
import {makeStyles, TableCell, TableRow} from "@material-ui/core";
import iconWorksReducer from "libs/iconWorksReducer";
import DateFormats from "constants/DateFormats";
import moment from "libs/moment";
import WorkableIconDownloadButton from "components/button/WorkableIconDownloadButton";
import WorkableIconPreviewButton from "components/button/WorkableIconPreviewButton";
import WorkableIconDownloadCountButton from "components/button/WorkableIconDownloadCountButton";
import WorkableIconUnpublishButton from "components/button/WorkableIconUnpublishButton";
import WorkableIconPublishButton from "components/button/WorkableIconPublishButton";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import {createDownloadHistoryFile} from "components/popup/DownloadHistoryPopup";
import Tr from "translations/Translations";
import {createEditDescriptionFile} from "components/popup/EditDescriptionPopup";
import WorkableIconSearchButton from "components/button/WorkableIconSearchButton";
import WorkableIconEditButton from "../../../../../../../../components/button/WorkableIconEditButton";
import {
    createEditNoticeReferenceNumberFile
} from "../../../../../../../../components/complex/documentsTables/swz/AddNoticeReferenceNumberPopup";

const useStyles = makeStyles(theme => ({
    buttonColumn: theme.components.table.buttonColumn,
    descriptionTooltip: {
        whiteSpace: 'pre-line'
    }
}))

function AttachmentResultNoticeRow(
    {
        demandId,
        editable,
        resultNotice,
        attachment,
        onEditDescriptionFile,
        onDownloadHistory,
        onEditNoticeReferenceNumberFile
    }) {
    const classes = useStyles();

    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        preview: false,
        download: false,
        publish: false,
        downloadCount: false,
        delete: false
    });

    const onDownloadCount = () => {
        onDownloadHistory(createDownloadHistoryFile(attachment.name, demandId, ResultNoticeService.buildIdentity(resultNotice.identity, attachment.identity)))
    };


    const downloadFile = (file, openInBrowser) => {
        dispatchIconWorks({type: openInBrowser ? 'preview' : 'download', works: true})
        ResultNoticeService.getDownloadFile(demandId, resultNotice.identity, file.identity, openInBrowser)
            .finally(() => dispatchIconWorks({type: openInBrowser ? 'preview' : 'download', works: false}));
    };

    const deleteResultNotice = (file) => {
        dispatchIconWorks({type: 'delete', works: true})
        ResultNoticeService.deleteResultNotice(demandId, resultNotice.identity, file.identity).finally(() => dispatchIconWorks({
            type: 'delete',
            works: false
        }));
    };

    const noticePublication = (file, publish) => {
        dispatchIconWorks({type: 'publish', works: true})
        const func = publish ? ResultNoticeService.publishResultNoticeAttachment : ResultNoticeService.unpublishResultNoticeAttachment;
        func(demandId, resultNotice.identity, file.identity).finally(() => dispatchIconWorks({type: 'publish', works: false}));
    };

    return (
        <TableRow key={attachment.identity}>
            <TableCell>{attachment.name}</TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconSearchButton
                    onClick={() => onEditDescriptionFile(createEditDescriptionFile(demandId, ResultNoticeService.buildIdentity(resultNotice.identity, attachment.identity),
                        attachment.name, attachment.description, !editable, false))}
                    tooltip={attachment.description ? <p className={classes.descriptionTooltip}>{attachment.description}</p> : <i>{Tr('no_description')}</i>}
                    disabled={iconWorks.anyWorks}
                />
            </TableCell>
            <TableCell>{moment(attachment.createDate).format(DateFormats.DATETIME_WITH_SECONDS_FORMAT)}</TableCell>
            <TableCell>{attachment.publishDate && moment(attachment.publishDate).format(DateFormats.DATETIME_WITH_SECONDS_FORMAT)}</TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconEditButton
                    tooltip={Tr('enter_notice_reference_number')}
                    onClick={() => onEditNoticeReferenceNumberFile(createEditNoticeReferenceNumberFile(demandId, ResultNoticeService.buildIdentity(resultNotice.identity, attachment.identity),
                        attachment.noticeReferenceNumber))}
                    disabled={iconWorks.anyWorks}
                    working={iconWorks.enterNoticeReferenceNumber}
                />
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconPreviewButton
                    working={iconWorks.preview} disabled={!attachment.actions.preview || iconWorks.anyWorks}
                    onClick={() => downloadFile(attachment, true)}
                />
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconDownloadButton
                    working={iconWorks.download} disabled={!attachment.actions.download || iconWorks.anyWorks}
                    onClick={() => downloadFile(attachment, false)}/>
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                {
                    attachment.published ?
                        <WorkableIconUnpublishButton
                            working={iconWorks.publish} disabled={!editable || !attachment.actions.unpublish || iconWorks.anyWorks}
                            onClick={() => noticePublication(attachment, false)}
                        /> :
                        <WorkableIconPublishButton
                            working={iconWorks.publish} disabled={!editable || !attachment.actions.publish || iconWorks.anyWorks}
                            onClick={() => noticePublication(attachment, true)}
                        />
                }
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconDownloadCountButton
                    tooltip={Tr("download_count_x", {downloadCount: attachment.downloadCount})}
                    working={iconWorks.downloadCount}
                    disabled={!editable || !attachment.actions.downloadCount || iconWorks.anyWorks}
                    onClick={onDownloadCount}
                />

            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconDeleteButton
                    working={iconWorks.delete} disabled={!editable || !attachment.actions.delete || iconWorks.anyWorks}
                    onClick={() => deleteResultNotice(attachment)}
                />
            </TableCell>
        </TableRow>

    )
}

AttachmentResultNoticeRow.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    editable: PropTypes.bool.isRequired,
    resultNotice: PropTypes.object.isRequired,
    onEditDescriptionFile: PropTypes.func.isRequired,
    onDownloadHistory: PropTypes.func.isRequired
}


export default AttachmentResultNoticeRow;